<script setup lang="ts">
import { ref, computed, inject } from 'vue';

import { injectionKeys as litiumKeys } from '@motillo/drapejs-litium';
import ModalContainer from "@/components/ModalContainer.vue";
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import DecorationCard from './DecorationCard.vue';

const { websiteText } = useWebsiteTexts();
const emit = defineEmits(['selected', 'selectedValue']);
const toLitiumMediaUrl = inject(litiumKeys.toLitiumMediaUrlKey, () => '');
const registerModal = inject('registerModal');
const unregisterModal = inject('unregisterModal');

const props = defineProps({
  placements: {
    type: Array,
    required: true,
  },
  selected: {
    type: String,
    default: '',
  },
});

const mappedPlacements = computed(() => {
  return props.placements.map(p => {
    return {
      id: p.systemId,
      name: p.position.name || p.position.value,
      value: p.position.value,
      url: toLitiumMediaUrl(p.positionImageSystemId, { maxWidth: 359 }),
      area: p.printableArea || '',
    };
  });
});

const selectedPlacement = ref(props.selected);
const zoomModalOpen = ref(false);

function selectPlacement(id: string) {
  selectedPlacement.value = id;
  emit('selected', id);
}

function selectPlacementValue(value: string) {
  emit('selectedValue', value);
}

function openZoomModal() {
  registerModal('DecoratorRemove');
  zoomModalOpen.value = true;
}

function closeZoomModal() {
  zoomModalOpen.value = false;
  unregisterModal('DecoratorRemove');
}

function selectZoomedInPlacement(id: string) {
  selectPlacement(id);
  closeZoomModal();
}
</script>

<template>
  <div class="decoration-placement">
    <div class="decoration-placement__title-and-zoom">
      <p class="decoration-placement__title">
        {{ websiteText('decoration__choose_placement').value }}
      </p>
      <div
        class="decoration-placement__zoom"
        @click="openZoomModal"
      >
        <img src="@/static/icons/magnifying_glass.svg" alt="">
        {{ websiteText('global__zoom').value }}
      </div>
    </div>
    <div class="decoration-placement__container">
      <DecorationCard
        v-for="placement in mappedPlacements"
        :key="placement.id"
        :decoration="placement"
        :selected="placement.id === selectedPlacement"
        @selected="selectPlacement"
        @selected-value="selectPlacementValue"
      >
      </DecorationCard>
    </div>
    <ModalContainer
      v-if="zoomModalOpen"
      :show-exit-button="true"
      position="center"
      @close-modal="closeZoomModal"
    >
    <div class="decoration-zoom__modal">
      <p class="decoration-zoom__modal-title">
        {{ websiteText('decoration__choose_placement').value }}
      </p>
      <div class="decoration-zoom__modal-placement-container">
        <DecorationCard
          v-for="placement in mappedPlacements"
          :key="placement.id"
          :decoration="placement"
          :selected="placement.id === selectedPlacement"
          @selected="selectZoomedInPlacement"
          @selected-value="selectPlacementValue"
        >
        </DecorationCard>
      </div>
    </div>
    </ModalContainer>
  </div>
</template>

<style>
  .decoration-placement {
    margin-bottom: 25px;
  }

  .decoration-placement__title-and-zoom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .decoration-placement__title {
    margin: 0;
    font-size: 14px;
    color: var(--color-text-darker);
    user-select: none;
  }

  .decoration-placement__zoom {
    border-radius: 2px;
    border: 1px solid var(--color-neutrals-100);
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    cursor: pointer;
  }

  .decoration-placement__container {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 20px;
  }

  .decoration-zoom__modal {
    padding: 10px 10px 15px;
  }

  .decoration-zoom__modal-title {
    margin: 0 0 20px;
    font-size: 20px;
    line-height: 26px;
  }

  .decoration-zoom__modal-placement-container {
    display: grid;
    gap: 10px;
  }

  @media (--desktop) {
    .decoration-placement .modal-container {
      max-width: 800px;
    }

    .decoration-zoom__modal {
      padding: 10px 30px 15px;
    }

    .decoration-zoom__modal-title {
      margin: 10px 0 20px;
    }

    .decoration-zoom__modal-placement-container {
      grid-template-columns: 1fr 1fr;
      gap: 20px;
    }
  }

  @media (--tabletAndDesktop) {
    .decoration-placement .modal-container {
      overflow-y: auto;
    }
  }
</style>
